// Pure helpers for Work Plan staff flags — the configurable vocabulary (Settings → Work plan →
// Staff flags; served by GET /api/work-plan/flags and in the day bundle as `flags`) and the
// `flagIds` a posting carries. Framework-free so vitest covers it without a DOM; the planner
// and the settings editor stay thin over these.
//
// Colours reuse the 10-id palette shared by queue statuses and thread groups (GROUP_COLOR_PALETTE)
// — no new palette. Tailwind v4 needs COMPLETE literal class strings (no `text-${tone}-500`),
// hence the per-id FLAG_TONES table below.
import { GROUP_COLOR_PALETTE } from "@/lib/thread-groups";

/** Mirrors the server caps (storage/work_plan_flags.go). */
export const FLAG_LABEL_MAX = 40;
export const FLAG_DESCRIPTION_MAX = 160;
export const MAX_FLAGS = 24;
/** The server's icon-slug rule: permissive, the catalog lives client-side. */
export const FLAG_ICON_SLUG_RE = /^[a-z0-9-]{1,48}$/;
export const FLAG_ICON_FALLBACK = "flag";
export const FLAG_FALLBACK_COLOR_ID = "slate";

const glyphClasses = {
  violet: "text-violet-600 dark:text-violet-300",
  rose: "text-rose-600 dark:text-rose-300",
  amber: "text-amber-600 dark:text-amber-300",
  emerald: "text-emerald-600 dark:text-emerald-300",
  teal: "text-teal-600 dark:text-teal-300",
  sky: "text-sky-600 dark:text-sky-300",
  blue: "text-blue-600 dark:text-blue-300",
  fuchsia: "text-fuchsia-600 dark:text-fuchsia-300",
  orange: "text-orange-600 dark:text-orange-300",
  slate: "text-slate-500 dark:text-slate-300",
};

const softClasses = {
  violet: "bg-violet-100 dark:bg-violet-950/60",
  rose: "bg-rose-100 dark:bg-rose-950/60",
  amber: "bg-amber-100 dark:bg-amber-950/60",
  emerald: "bg-emerald-100 dark:bg-emerald-950/60",
  teal: "bg-teal-100 dark:bg-teal-950/60",
  sky: "bg-sky-100 dark:bg-sky-950/60",
  blue: "bg-blue-100 dark:bg-blue-950/60",
  fuchsia: "bg-fuchsia-100 dark:bg-fuchsia-950/60",
  orange: "bg-orange-100 dark:bg-orange-950/60",
  slate: "bg-slate-100 dark:bg-slate-900/60",
};

/**
 * Per palette id: `glyph` (icon colour), `chip` (the palette's bordered chip), `dot` (solid
 * swatch) and `soft` (tinted background behind a glyph). Every value is a complete literal.
 */
export const FLAG_TONES = Object.freeze(
  Object.fromEntries(
    GROUP_COLOR_PALETTE.map((entry) => [
      entry.id,
      Object.freeze({
        id: entry.id,
        label: entry.label,
        glyph: glyphClasses[entry.id] || glyphClasses.slate,
        chip: entry.chipClassName,
        dot: entry.dotClassName,
        soft: softClasses[entry.id] || softClasses.slate,
      }),
    ]),
  ),
);

const VALID_COLOR_IDS = new Set(GROUP_COLOR_PALETTE.map((entry) => entry.id));

/** Tone for a colour id, slate for anything unknown (the server normalises the same way). */
export function flagTone(colorId) {
  const id = String(colorId || "").trim();
  return FLAG_TONES[id] || FLAG_TONES[FLAG_FALLBACK_COLOR_ID];
}

export function normalizeFlagColorId(colorId) {
  const id = String(colorId || "").trim();
  return VALID_COLOR_IDS.has(id) ? id : FLAG_FALLBACK_COLOR_ID;
}

/**
 * Slug id derived from a label — the same rule the server applies when a row arrives without
 * an id (work roles AND flags share it: lowercase a–z/0–9 runs joined by "-", ≤32 chars). Used
 * to preview the id a new row will get and to catch two rows colliding before the PUT.
 */
export function slugFromLabel(label) {
  return String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32)
    .replace(/-+$/g, "");
}

/** Cuts a string to `max` code points (the server counts runes, not UTF-16 units). */
function clampRunes(value, max) {
  const runes = Array.from(String(value || "").trim());
  return runes.length > max ? runes.slice(0, max).join("") : runes.join("");
}

export function normalizeFlagIcon(icon) {
  const slug = String(icon || "").trim();
  return FLAG_ICON_SLUG_RE.test(slug) ? slug : FLAG_ICON_FALLBACK;
}

/**
 * A clean vocabulary from whatever the server (or a stale cache) sent: non-array → [], rows
 * without an id dropped, label/description trimmed + capped, icon slug validated (else the
 * fallback glyph), colour validated (else slate), duplicate ids dropped, capped at MAX_FLAGS.
 */
export function normalizeFlagDefs(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const item of raw) {
    const id = String(item?.id || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      label: clampRunes(item?.label, FLAG_LABEL_MAX) || id,
      description: clampRunes(item?.description, FLAG_DESCRIPTION_MAX),
      icon: normalizeFlagIcon(item?.icon),
      colorId: normalizeFlagColorId(item?.colorId),
    });
    if (out.length >= MAX_FLAGS) break;
  }
  return out;
}

/**
 * The planner's view of the vocabulary: `list` (normalised, vocabulary order), `byId`, and
 * `supported` — false when the bundle has no `flags` field at all (older backend), which is
 * why callers must pass the RAW field and never default it to [].
 */
export function buildFlagView(rawFlags) {
  const list = normalizeFlagDefs(rawFlags);
  const byId = {};
  for (const flag of list) byId[flag.id] = flag;
  return { list, byId, supported: Array.isArray(rawFlags) };
}

/** A posting's flag ids as a clean string array (missing field → []). */
export function postingFlagIds(posting) {
  const raw = posting?.flagIds;
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const value of raw) {
    const id = String(value || "").trim();
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * The flag definitions a posting carries, in VOCABULARY order (so glyphs line up the same way
 * on every line); ids no longer in the vocabulary are skipped, never rendered raw. Accepts the
 * buildFlagView result or a bare id → def map.
 */
export function resolvePostingFlags(posting, viewOrById) {
  const ids = new Set(postingFlagIds(posting));
  if (ids.size === 0) return [];
  const defs = Array.isArray(viewOrById?.list)
    ? viewOrById.list
    : Object.values(viewOrById || {}).filter((def) => def && def.id);
  return defs.filter((def) => ids.has(def.id));
}

/** Next id list after flipping one flag on or off (order preserved, blanks dropped). */
export function toggleFlagId(flagIds, flagId) {
  const current = postingFlagIds({ flagIds });
  const id = String(flagId || "").trim();
  if (!id) return current;
  return current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id];
}

/** Starter rows the empty flags editor offers — UI suggestions only, the server seeds just stay-back. */
export const DEFAULT_FLAG_SUGGESTIONS = Object.freeze([
  { label: "Stay back roster", description: "Stays until the last patient has left.", icon: "moon-star", colorId: "violet" },
  { label: "Key holder", description: "Holds the clinic keys today.", icon: "key-round", colorId: "amber" },
  { label: "Opening duty", description: "Opens up and sets the clinic before the first patient.", icon: "sunrise", colorId: "sky" },
  { label: "Closing duty", description: "Locks up after the last patient.", icon: "sunset", colorId: "orange" },
  { label: "On call", description: "Reachable for urgent cover.", icon: "phone-call", colorId: "emerald" },
]);
